import { commasIndianFormat } from '@utils/textHelper';
import {
  BundleAddOn,
  hasMultiGoalBundleAddOns
} from '@cont/Checkout/bundleAddOnsHelper';
import AddonCard from './AddonCard';
import MultiGoalComboAddonCard from './MultiGoalComboAddonCard';

interface AddonsInfoType {
  iconicFeatures: Record<string, any>;
}

const getPriceBreakDown = (addOnData, referral) => {
  const { code, discountPercent } = referral ?? {};
  const { add_for, originally_for } = addOnData;
  const comboDiscount = Math.round(originally_for - add_for);
  const referralDiscount = Math.round((add_for * discountPercent) / 100);
  const priceBreakDown = [
    [
      {
        name: 'Original price',
        price: `₹${commasIndianFormat(originally_for)}`,
        type: 'positive'
      },
      {
        name: 'Combo discount',
        price: `-₹${commasIndianFormat(comboDiscount)}`,
        type: 'negative'
      }
    ]
  ];
  if (code)
    priceBreakDown.push([
      {
        name: 'Discounted price',
        price: `₹${commasIndianFormat(add_for)}`,
        type: 'positive'
      },
      {
        name: `‘${code}’ code applied`,
        price: `-₹${commasIndianFormat(referralDiscount)}`,
        type: 'negative'
      }
    ]);

  priceBreakDown.push([
    {
      name: 'Final price',
      price: `₹${commasIndianFormat(Math.round(add_for))}`,
      type: 'positive'
    }
  ]);
  return priceBreakDown;
};

interface AddonPickerCardsProps {
  bundleAddonData: BundleAddOn[];
  isIconicProSingleForced: boolean;
  iconicProForcedBundle: BundleAddOn | null;
  skuDetailsForForcedBundle: Record<string, any>;
  skuDetailsForList: Record<string, any>;
  addonsMetaData?: Array<Record<string, any> | undefined>;
  selectedAddonUid?: string;
  hasMultipleAddon: boolean;
  referral: Record<string, any>;
  primaryDisplayDuration?: string;
  onSelectAddon: (addOnData: BundleAddOn) => void;
}

/**
 * Renders the optional add-on / combo cards for the Iconic Pro Add-ons step.
 * Multi-goal combo cards are used whenever any bundle spans more
 * than one goal (see hasMultiGoalBundleAddOns); otherwise falls back to the
 * existing single-goal AddonCard list. Section title stays unchanged.
 */
const AddonPickerCards = ({
  bundleAddonData,
  isIconicProSingleForced,
  iconicProForcedBundle,
  skuDetailsForForcedBundle,
  skuDetailsForList,
  addonsMetaData,
  selectedAddonUid,
  hasMultipleAddon,
  referral,
  primaryDisplayDuration,
  onSelectAddon
}: AddonPickerCardsProps) => {
  const isMultiGoalComboUi = hasMultiGoalBundleAddOns(bundleAddonData);

  if (isIconicProSingleForced && iconicProForcedBundle) {
    if (hasMultiGoalBundleAddOns([iconicProForcedBundle])) {
      return (
        <MultiGoalComboAddonCard
          bundle={iconicProForcedBundle}
          index={0}
          isChecked
          disabled
          durationText={primaryDisplayDuration}
          onClick={() => {}}
        />
      );
    }

    const forcedMetaInfo =
      addonsMetaData?.find(
        (metaData) => metaData?.goalUid === iconicProForcedBundle.uid
      ) || {};

    return (
      <AddonCard
        title={iconicProForcedBundle.name}
        isChecked
        showRadioButton={false}
        onClick={() => {}}
        priceBreakDown={getPriceBreakDown(iconicProForcedBundle, referral)}
        skuDetails={skuDetailsForForcedBundle}
        comboDiscount={Math.round(
          iconicProForcedBundle.originally_for - iconicProForcedBundle.add_for
        )}
        addonsMetaInfo={forcedMetaInfo as AddonsInfoType}
      />
    );
  }

  return (
    <>
      {bundleAddonData?.map((addOnData, addOnIndex) => {
        const isChecked = addOnData?.uid === selectedAddonUid;

        if (isMultiGoalComboUi) {
          return (
            <MultiGoalComboAddonCard
              key={addOnData?.uid}
              bundle={addOnData}
              index={addOnIndex}
              isChecked={isChecked}
              durationText={primaryDisplayDuration}
              onClick={() => onSelectAddon(addOnData)}
            />
          );
        }

        const addonsMetaInfo = addonsMetaData
          ? addonsMetaData?.find(
              (metaData) => metaData?.goalUid === addOnData?.uid
            )
          : {};
        const { add_for, originally_for } = addOnData;

        return (
          <AddonCard
            key={addOnData?.uid}
            onClick={() => onSelectAddon(addOnData)}
            showRadioButton={hasMultipleAddon}
            title={addOnData?.name}
            isChecked={isChecked}
            priceBreakDown={getPriceBreakDown(addOnData, referral)}
            skuDetails={skuDetailsForList}
            comboDiscount={Math.round(originally_for - add_for)}
            addonsMetaInfo={addonsMetaInfo as AddonsInfoType}
          />
        );
      })}
    </>
  );
};

export default AddonPickerCards;
